import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Send, Paperclip, Check, CheckCheck } from 'lucide-react';
import { apiService } from '../../services/api';
import { useClientAuth } from '../../contexts/ClientAuthContext';
import { useSocket } from '../../contexts/SocketContext';
import { notificationService } from '../../services/notificationService';

interface Message {
  _id: string;
  content: string;
  senderType: 'admin' | 'applicant';
  sender: {
    admin?: {
      _id: string;
      email: string;
      profile?: {
        firstName: string;
        lastName: string;
      };
    };
    applicant?: {
      name: string;
      email: string;
    };
  };
  sentAt: string;
  isRead: boolean;
  attachments?: any[];
}

const ClientMessages: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { client } = useClientAuth();
  
  const [messages, setMessages] = useState<Message[]>([]);
  const [threadId, setThreadId] = useState<string | null>(null);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [typingUsers, setTypingUsers] = useState<Set<string>>(new Set());
  const [isTyping, setIsTyping] = useState(false);
  const [typingTimeout, setTypingTimeout] = useState<NodeJS.Timeout | null>(null);
  
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messageInputRef = useRef<HTMLInputElement>(null);
  
  const { 
    isConnected, 
    joinThread, 
    leaveThread, 
    sendTypingStart, 
    sendTypingStop, 
    onNewMessage, 
    onUserTyping, 
    onUserStoppedTyping,
    offNewMessage,
    offUserTyping,
    offUserStoppedTyping
  } = useSocket();

  useEffect(() => {
    if (id) {
      fetchMessages();
      requestNotificationPermission();
      
      // Set up real-time listeners
      onNewMessage(handleNewMessage);
      onUserTyping(handleUserTyping);
      onUserStoppedTyping(handleUserStoppedTyping);
      
      return () => {
        if (threadId) {
          leaveThread(threadId);
        }
        offNewMessage();
        offUserTyping();
        offUserStoppedTyping();
      };
    }
  }, [id]);

  useEffect(() => {
    if (threadId) {
      joinThread(threadId);
      return () => {
        leaveThread(threadId);
      };
    }
  }, [threadId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const requestNotificationPermission = async () => {
    await notificationService.requestPermission();
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchMessages = async () => {
    if (!id) return;
    
    try {
      const response = await apiService.getApplicationMessages(id);
      if (response.status === 'success') {
        console.log('=== CLIENT: Fetching messages ===');
        console.log('Response:', response);
        console.log('Thread ID:', response.data.thread._id);
        setMessages(response.data.messages);
        setThreadId(response.data.thread._id);
      } else {
        setError('Failed to load messages');
      }
    } catch (err) {
      setError('Failed to load messages');
    } finally {
      setLoading(false);
    }
  };

  const handleNewMessage = (data: any) => {
    console.log('=== CLIENT: New message received ===');
    console.log('Data:', data);
    console.log('Current threadId:', threadId);
    console.log('Message threadId:', data.threadId);
    console.log('Match:', data.threadId === threadId);
    
    if (data.threadId === threadId) {
      console.log('Adding message to state');
      setMessages(prev => [...prev, data.message]);
      
      // Show notification if message is from admin
      if (data.senderType === 'admin') {
        const senderName = data.message.sender.admin?.profile?.firstName && data.message.sender.admin?.profile?.lastName
          ? `${data.message.sender.admin.profile.firstName} ${data.message.sender.admin.profile.lastName}`
          : data.message.sender.admin?.email || 'Admin';
        notificationService.showMessageNotification(senderName, data.message.content, data.threadId);
      }
    } else {
      console.log('Message threadId does not match current threadId');
    }
  };

  const handleUserTyping = (data: any) => {
    setTypingUsers(prev => new Set([...prev, data.userName]));
  };

  const handleUserStoppedTyping = (data: any) => {
    setTypingUsers(prev => {
      const newSet = new Set(prev);
      newSet.delete(data.userName);
      return newSet;
    });
  };

  const handleTyping = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNewMessage(e.target.value);
    
    if (!isTyping && threadId) {
      setIsTyping(true);
      sendTypingStart(threadId, client?.name || 'Applicant', 'applicant');
    }
    
    if (typingTimeout) {
      clearTimeout(typingTimeout);
    }
    
    const timeout = setTimeout(() => {
      setIsTyping(false);
      if (threadId) {
        sendTypingStop(threadId, client?.name || 'Applicant', 'applicant');
      }
    }, 1000);
    
    setTypingTimeout(timeout);
  };

  const sendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMessage.trim() || !id || sending) return;

    setSending(true);
    try {
      const response = await apiService.sendMessage(id, newMessage.trim());
      if (response.status === 'success') {
        setNewMessage('');
        // Don't add message manually - it will come through real-time event
        
        // Stop typing indicator
        if (isTyping) {
          setIsTyping(false);
          sendTypingStop(threadId, client?.name || 'Applicant', 'applicant');
        }
      } else {
        setError('Failed to send message');
      }
    } catch (err) {
      setError('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const getSenderName = (message: Message) => {
    if (message.senderType === 'admin' && message.sender.admin) {
      const profile = message.sender.admin.profile;
      if (profile?.firstName && profile?.lastName) {
        return `${profile.firstName} ${profile.lastName}`;
      }
      return message.sender.admin.email;
    } else if (message.senderType === 'applicant') {
      return message.sender.applicant?.name || 'You';
    }
    return 'Unknown';
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const getMessageStatus = (message: Message) => {
    if (message.senderType === 'applicant') {
      if (message.isRead) {
        return <CheckCheck className="w-4 h-4 text-green-500" />;
      } else {
        return <Check className="w-4 h-4 text-gray-400" />;
      }
    }
    return null;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading messages...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate(`/client/applications/${id}`)}
            className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back to Application
          </button>
          
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Messages</h1>
              <p className="text-gray-600 mt-1">Communicate with our team about your application</p>
            </div>
            <div className="flex items-center">
              <div className={`w-2 h-2 rounded-full mr-2 ${isConnected ? 'bg-green-500' : 'bg-red-500'}`}></div>
              <span className="text-sm text-gray-500">
                {isConnected ? 'Connected' : 'Disconnected'}
              </span>
            </div>
          </div>
        </div>

        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md">
            {error}
          </div>
        )}

        {/* Chat Interface */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 h-[calc(100vh-250px)] flex flex-col">
          {/* Chat Header */}
          <div className="p-4 border-b border-gray-200 bg-white">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-medium text-gray-900">Support Team</h3>
                <p className="text-sm text-gray-500">We're here to help with your application</p>
              </div>
            </div>
          </div>

          {/* Messages Area */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
            {messages.length === 0 ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Send className="w-8 h-8 text-primary" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Start a conversation</h3>
                  <p className="text-gray-500">Send a message to get help with your application</p>
                </div>
              </div>
            ) : (
              <>
                {messages.map((message) => (
                  <div
                    key={message._id}
                    className={`flex ${message.senderType === 'applicant' ? 'justify-end' : 'justify-start'}`}
                  >
                      <div
                        className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${
                          message.senderType === 'applicant'
                            ? 'bg-primary text-white'
                            : 'bg-white text-gray-900 border border-gray-200'
                        }`}
                      >
                      <p className="text-sm">{message.content}</p>
                      <div className="flex items-center justify-between mt-1">
                        <span className={`text-xs ${message.senderType === 'applicant' ? 'text-green-100' : 'text-gray-500'}`}>
                          {formatTime(message.sentAt)}
                        </span>
                        {getMessageStatus(message)}
                      </div>
                    </div>
                  </div>
                ))}
                
                {/* Typing Indicator */}
                {typingUsers.size > 0 && (
                  <div className="flex justify-start">
                    <div className="bg-white border border-gray-200 rounded-lg px-4 py-2">
                      <div className="flex items-center space-x-1">
                        <div className="flex space-x-1">
                          <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                          <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                          <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                        </div>
                        <span className="text-xs text-gray-500 ml-2">
                          {Array.from(typingUsers).join(', ')} typing...
                        </span>
                      </div>
                    </div>
                  </div>
                )}
                
                <div ref={messagesEndRef} />
              </>
            )}
          </div>

          {/* Message Input */}
          <div className="p-4 border-t border-gray-200 bg-white">
            <form onSubmit={sendMessage} className="flex items-center space-x-2">
              <button
                type="button"
                className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <Paperclip className="w-5 h-5" />
              </button>
              <input
                ref={messageInputRef}
                type="text"
                value={newMessage}
                onChange={handleTyping}
                placeholder="Type your message..."
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                disabled={sending}
              />
              <button
                type="submit"
                disabled={!newMessage.trim() || sending}
                className="p-2 bg-primary text-white rounded-lg hover:bg-primary-dark disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {sending ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  <Send className="w-5 h-5" />
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClientMessages;